import styled from "styled-components";
import { Button } from "shared/components/Button";
import Refresh from "shared/components/Refresh";
import Search from "shared/components/Search";
import useNoticeListView from "features/owner/hooks/useNoticeListView";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ListViewProps {
    clickedItem: {
        id: string;
        writer: string;
    };
    onNoticeClick: (id: string, writer: string) => void;
    isBranch: boolean;
    onCreateClickHandler: () => void;
}

const NoticeListView = ({ clickedItem, onNoticeClick, isBranch, onCreateClickHandler }: ListViewProps) => {
    const { onRefreshClick, search, searchedLists, onChange } = useNoticeListView(isBranch);

    return (
        <Container>
            <Header>
                <div>
                    <Title>공지 목록</Title>
                    <Sub>제목을 눌러 내용을 확인하세요.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Search placeholder="제목을 입력해 주세요" value={search} onChange={onChange} />
            <List>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "공지가 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 제목으로 다시 검색해 보세요." : "생성하기를 눌러 공지를 작성하세요."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => (
                        <Item
                            key={item.id}
                            $on={item.id === clickedItem.id}
                            onClick={() => onNoticeClick(item.id, item.writer)}
                        >
                            <ItemTitle>{item.title}</ItemTitle>
                            <ItemMeta>{item.writer === "master" ? "본사" : "학원"}</ItemMeta>
                        </Item>
                    ))
                )}
            </List>
            <Buttons>
                <Button onButtonClick={onCreateClickHandler} name="생성하기" />
            </Buttons>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-radius: 12px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar}
`;

const Item = styled.div<{ $on: boolean }>`
    padding: 12px 14px;
    border-bottom: 1px solid ${hw.border};
    background: ${props => props.$on ? hw.brandSoft : hw.surface};
    box-shadow: ${props => props.$on ? `inset 3px 0 0 ${hw.brand}` : "none"};
    cursor: pointer;

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const ItemTitle = styled.span`
    display: block;
    color: ${hw.text};
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ItemMeta = styled.span`
    display: block;
    margin-top: 4px;
    color: ${hw.textSubtle};
    font-size: 12px;
    font-weight: 600;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

const Buttons = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
`;

export default NoticeListView;
